import * as React from 'react';
import { UserContext } from '../Context/UserContext';
import { CommissionStatementResultRepository } from '../../MedicareProAPI/Repositories/CommissionStatement/CommissionStatementResultRepository';
import type {
  CommissionStatementResult,
  CommissionStatementResultSummary,
} from '../../MedicareProAPI/Entities/CommissionStatement/CommissionStatementResult';
import { CommissionStatementResultSummaryGroup } from '../../MedicareProAPI/Entities/CommissionStatement/CommissionStatementResult';
import { Utilities } from '../../MedicareProAPI/Utilities/Utilities';
import type { User } from '../../MedicareProAPI/Entities/User/User';
import StyledModal from '../Components/StyledModal';

type ProcessingLogDay = {
  reference: string;
  date: Date;
  entries: Array<ProcessingLogEntry>;
};

type ProcessingLogEntry = {
  dateTime: Date;
  data: Array<ProcessingLogEntryGroup>;
};

type ProcessingLogEntryGroup = {
  group: CommissionStatementResultSummaryGroup;
  results: Array<CommissionStatementResultSummary>;
};

interface RenderSingleResultProps {
  result: CommissionStatementResultSummary;
}

function RenderSingleResult({ result }: RenderSingleResultProps): React.JSX.Element {
  const [showDetails, setShowDetails] = React.useState<boolean>(false);

  return (
    <>
      <p className="mb-0 mt-1">
        <span dangerouslySetInnerHTML={{ __html: result.description }} />
        {result.detail && (
          <i
            title={showDetails ? 'Hide details' : 'Show details'}
            onClick={(e) => setShowDetails(!showDetails)}
            className="fas fa-info-circle ml-2"
          ></i>
        )}
      </p>
      {showDetails && result.detail && (
        <p className="small mb-0 mt-0">
          {result.detail.map((detailSingle) => (
            <>
              <strong>{detailSingle.label}</strong>: {detailSingle.value}
              <br />
            </>
          ))}
        </p>
      )}
    </>
  );
}

interface RenderSingleEntryProps {
  entry: ProcessingLogEntry;
}

function RenderSingleEntry({ entry }: RenderSingleEntryProps): React.JSX.Element {
  const [showGroupedModal, setShowGroupedModal] = React.useState<boolean>(false);
  const [groupedModalResults, setGroupedModalResults] =
    React.useState<Array<CommissionStatementResultSummary>>();

  const closeGroupedModal = () => {
    setShowGroupedModal(false);
    setGroupedModalResults(null);
  };

  const openGroupedModal = (groupResult: Array<CommissionStatementResultSummary>) => {
    setGroupedModalResults(groupResult);
    setShowGroupedModal(true);
  };

  const groupLabel = (
    groupType: CommissionStatementResultSummaryGroup,
    total: number
  ): React.JSX.Element => {
    if (groupType === CommissionStatementResultSummaryGroup.IND_POLICY_COMP_HISTORY_INSERT) {
      return <>Inserted Comp History for {total} clients</>;
    }
  };

  return (
    <div className="alert alert-info small mb-0 mt-2">
      <StyledModal
        size="lg"
        title={'Process Log Detail'}
        footerCloseButton={true}
        show={showGroupedModal}
        onClose={closeGroupedModal}
      >
        <div className="alert alert-info small mb-0 mt-2">
          {groupedModalResults &&
            groupedModalResults.map((result, resultKey) => (
              <RenderSingleResult key={resultKey} result={result} />
            ))}
        </div>
      </StyledModal>
      <p className="mb-0">
        <strong>{Utilities.renderReadableTime(entry.dateTime)}</strong>
      </p>
      {// Loop through each group
      entry.data?.map((entryGroup, entryGroupKey) => {
        // If there are multiple instances of this action then display the data as expected
        const totalResults = entryGroup.results.length;

        if (totalResults > 1) {
          const label = groupLabel(entryGroup.group, totalResults);
          return (
            <p key={entryGroupKey} className="mb-0">
              {label}{' '}
              <button
                onClick={(e) => openGroupedModal(entryGroup.results)}
                className="cp badge badge-info border-0"
              >
                Show More
              </button>
            </p>
          );
        } else {
          const singleEntry = entryGroup.results[0];
          return <RenderSingleResult key={entryGroupKey} result={singleEntry} />;
        }
      })}
    </div>
  );
}
interface RenderResultProps {
  result: ProcessingLogDay;
}

function RenderResult({ result }: RenderResultProps): React.JSX.Element {
  return (
    <div>
      <h4 className="mb-0">{Utilities.renderReadableDate(result.date)}</h4>
      {
        // Loop through each result
        result.entries.map((entry, entryKey) => (
          <RenderSingleEntry key={entryKey} entry={entry} />
        ))
      }
    </div>
  );
}

export interface AgentCommissionStatementProcessLogProps {
  user: User;
}

export default function AgentCommissionStatementProcessLogPanel(
  props: AgentCommissionStatementProcessLogProps
): React.JSX.Element {
  const userData = React.useContext(UserContext);
  const [results, setResults] = React.useState<Array<ProcessingLogDay>>(null);
  const [resultsLoading, setResultsLoading] = React.useState<boolean>(true);

  const resultRepository = new CommissionStatementResultRepository();
  resultRepository.setAccessToken(userData.userAccessToken);

  const groupResult = (
    items: Array<CommissionStatementResultSummary>
  ): Array<ProcessingLogEntryGroup> => {
    const grouped: ProcessingLogEntryGroup[] = Object.values(
      items.reduce(
        (acc, item) => {
          if (!acc[item.group]) {
            acc[item.group] = {
              group: item.group,
              results: [],
            };
          }

          acc[item.group].results.push(item);

          return acc;
        },
        {} as Record<string, ProcessingLogEntryGroup>
      )
    );

    return grouped;
  };

  const loadResults = (): Promise<Array<CommissionStatementResult>> => {
    setResultsLoading(true);

    return resultRepository
      .getCommissionStatementsResults(props.user.getUserId())
      .then((resultCollection) => {
        const newResults = resultCollection.getItems();

        const newProcessingLog: Array<ProcessingLogDay> = [];

        newResults.forEach((resultsingle) => {
          // String reference for the day
          const dateReference = Utilities.dateToDateString(resultsingle.getDateTimeCreated());

          // See if there is already a log for this day.
          const existingLog = newProcessingLog.find(
            (newProcessingLogSingle) => newProcessingLogSingle.reference === dateReference
          );

          // Log entry data to be added to day
          const newEntry: ProcessingLogEntry = {
            dateTime: resultsingle.getDateTimeCreated(),
            data: groupResult(resultsingle.getSummary()),
          };

          // Already an instance of this day in the result set
          if (existingLog) {
            existingLog.entries.push(newEntry);
          }
          // Add the new day
          else {
            const newDay: ProcessingLogDay = {
              reference: dateReference,
              date: resultsingle.getDateTimeCreated(),
              entries: [newEntry],
            };

            newProcessingLog.push(newDay);
          }
        });

        setResults(newProcessingLog);
        setResultsLoading(false);

        return newResults;
      });
  };

  React.useEffect(() => {
    loadResults();
  }, []);

  return (
    <>
      <div className="card pb-0 pt-0" style={{ height: 'auto' }}>
        <div className="row">
          {resultsLoading === true && (!results || results.length === 0) && (
            <div className="col-12">
              <p className="alert alert-info small m-2">
                Loading... <i className="fas fa-spinner fa-spin"></i>
              </p>
            </div>
          )}
          {resultsLoading === false && (!results || results.length === 0) && (
            <div className="col-12">
              <p className="alert alert-info small m-2">No results to display</p>
            </div>
          )}
          {results && results.length > 0 && (
            <div className="col-12">
              <div className="p-2">
                {results.map((result, resultKey) => (
                  <RenderResult key={resultKey} result={result} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
